<template>
    <div>
      <Head :title="__(title)" />
      <div
        class="flex flex-col md:flex-row gap-3 mb-4 justify-between items-center ticket-filters"
      >
        <search-input
          v-model="form.search"
          placeholder="Search by Key, Subject, Priority, Status, Assign to..."
          class="w-full max-w-md search"
          @reset="reset"
        ></search-input>

        <div class="filter-add-new flex flex-col gap-3 md:flex-row items-center">
          <a
            v-if="export_import_allowed"
            class="uppercase gap-[1px] text-sm px-3 py-2 flex items-center justify-center"
            href="/dashboard/ticket/csv/export"
          >
            <img
              class="w-6 h-6"
              src="/images/svg/export-csv.svg"
              alt="Export CSV"
            />
            <span>{{ __('Export') }}</span>
          </a>
          <label
            v-if="export_import_allowed"
            for="importCSV"
            class="uppercase gap-[1px] text-sm px-3 py-2 flex items-center justify-center"
          >
            <img
              class="w-6 h-6"
              src="/images/svg/import-csv.svg"
              alt="Import CSV"
            />
            <span>{{ __('Import') }}</span>
            <input
              @change="uploadImportCSV"
              class="hidden"
              id="importCSV"
              type="file"
            />
          </label>
          <select
            v-model="form.limit"
            class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
          <Link 
            class="btn-indigo" 
            :href="filters.customer_id 
              ? route('tickets.create', { customer_id: filters.customer_id }) 
              : route('customers')"
          >
            <span>{{ __('New Ticket') }}</span>
          </Link>
        </div>
      </div>
      <div
        class="flex flex-col gap-3 mb-4 md:flex-row w-full items-center ticket-filters"
      >
        <div class="mr-2 w-full">{{ __('Filter Ticket By') }}:</div>
        <select-input v-model="form.type_id" class="mr-2 w-full">
          <option :value="null">{{ __('Type') }}</option>
          <option v-for="s in types" :key="s.id" :value="s.id">{{ s.name }}</option>
        </select-input>
        <button 
          v-if="form.type_id" 
          @click="resetFormKey('type_id')" 
          class="ml-2 text-red-500 hover:text-red-700"
          title="Reset"
        >
          ✖
        </button>

        <select-input v-model="form.category_id" class="mr-2 w-full" >
          <option :value="null">{{ __('Category') }}</option>
          <option v-for="s in categories" :key="s.id" :value="s.id">{{ s.name }}</option>
        </select-input>
        <button 
          v-if="form.category_id" 
          @click="resetFormKey('category_id')" 
          class="ml-2 text-red-500 hover:text-red-700"
          title="Reset"
        >
          ✖
        </button>

        <select-input v-model="form.department_id" class="mr-2 w-full" v-if="!(hidden_fields && hidden_fields.includes('department_id')) && user_access.ticket.update">
          <option :value="null">{{ __('Department') }}</option>
          <option v-for="s in departments" :key="s.id" :value="s.id">{{ s.name }}</option>
        </select-input>
        <button 
          v-if="form.department_id" 
          @click="resetFormKey('department_id')" 
          class="ml-2 text-red-500 hover:text-red-700"
          title="Reset"
        >
          ✖
        </button>

        <select-input v-model="form.priority_id" class="mr-2 w-full" v-if="!(hidden_fields && hidden_fields.includes('priority_id')) && user_access.ticket.update">
          <option :value="null">{{ __('Priority') }}</option>
          <option v-for="s in priorities" :key="s.id" :value="s.id">{{ s.name }}</option>
        </select-input>
        <button 
          v-if="form.priority_id" 
          @click="resetFormKey('priority_id')" 
          class="ml-2 text-red-500 hover:text-red-700"
          title="Reset"
        >
          ✖
        </button>

        <select-input v-model="form.status_id" class="mr-2 w-full" v-if="!(hidden_fields && hidden_fields.includes('status_id')) && user_access.ticket.update">
          
          <option :value="null">{{ __('Status') }}</option>
          <option v-for="s in statuses" :key="s.id" :value="s.id">{{ s.name }}</option>
        </select-input>
        <button 
          v-if="form.status_id" 
          @click="resetFormKey('status_id')" 
          class="ml-2 text-red-500 hover:text-red-700"
          title="Reset"
        >
          ✖
        </button>
        <select-input-filter
          :placeholder="__('Assign To')"
          :onInput="doFilter"
          @focus="doFilter"
          :items="assignees"
          v-if="!(hidden_fields && hidden_fields.includes('assigned_to')) && user_access.ticket.update"
          v-model="form.assigned_to"
          :key="form.assigned_to"
          :selectedValue="form.assigned_to"
          @update:modelValue="value => form.assigned_to = value"
          class="w-full"
        >
        </select-input-filter>
        <button 
          v-if="form.assigned_to" 
          @click="resetFormKey('assigned_to')" 
          class="ml-2 text-red-500 hover:text-red-700"
          title="Reset"
        >
          ✖
        </button>
      </div>
      <div
        class="flex flex-col gap-3 mb-4 md:flex-row w-full items-center ticket-filters"
      >

        <select-input-filter
            ref="customerDropdown"
            placeholder="Customers"
            :onInput="doFilterCustomers"
            @focus="doFilterCustomers"
            :items="customersData"
            v-model="form.customer_id"
            :key="form.customer_id"
            :selectedValue="form.customer_id" 
            @update:modelValue="value => form.customer_id = value"
            class="w-1/3"
        />
        <button 
          v-if="form.customer_id" 
          @click="resetFormKey('customer_id')" 
          class="ml-2 text-red-500 hover:text-red-700"
          title="Reset"
        >
          ✖
        </button>
      </div>
      <div class="bg-white rounded-md shadow overflow-x-auto">
        <div class="flex justify-between items-center p-4 bg-gray-100 rounded-md mb-4">
          <h2 class="text-lg font-semibold text-gray-700">
            {{ __('Total Tickets') }}: {{ total_tickets_amount }}
          </h2>
        </div>
        <table class="min-w-full whitespace-nowrap ticket_list">
          <tr class="text-left font-bold">
            <th v-for="(h, i) in headers" :key="i">
              <span
                :class="{'sort': h.sort, 'active' : form.field === h.name},form.direction"
                >{{ __(h.name) }}
                <span v-if="h.sort" class="icons">
                  <icon
                    class="fill-gray-300"
                    :class="{'fill-gray-800': (form.direction === 'desc' && form.field === h.value)}"
                    name="up"
                    @click="sort(h.value)"
                  />
                  <icon
                    class="fill-gray-300"
                    :class="{'fill-gray-800': form.direction === 'asc' && form.field === h.value}"
                    name="down"
                    @click="sort(h.value)"
                  />
                </span>
              </span>
            </th>
          </tr>
          <tr
            v-for="ticket in tickets.data"
            :key="ticket.id"
            class="hover:bg-gray-100 focus-within:bg-gray-100"
          >
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4 focus:text-indigo-500"
                :href="route('tickets.edit', ticket.uid || ticket.id)"
              >
                #{{ ticket.uid }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="s__details flex flex-col"
                :href="route('tickets.edit', ticket.uid || ticket.id)"
              >
                <span class="subject_t">{{ ticket.subject }}</span>
                <span class="user__d flex text-xs items-center pt-1">
                  <span
                    v-if="ticket.user"
                    class="user__n flex items-center pr-4"
                    title="Client"
                  >
                    <icon
                      name="user"
                      class="flex-shrink-0 h-3 fill-gray-400 pr-1"
                    />
                    {{ ticket.user }}
                  </span>
                  <span
                    v-if="ticket.assigned_to"
                    class="user__n flex items-center pr-4"
                    title="Assignee"
                  >
                    <icon
                      name="user-check"
                      class="flex-shrink-0 h-3 fill-gray-400 pr-1"
                    />
                    {{ ticket.assigned_to }}
                  </span>
                </span>
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4 focus:text-indigo-500"
                :href="route('tickets.edit', ticket.uid || ticket.id)"
              >
                {{ ticket.priority }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4 focus:text-indigo-500"
                :href="route('tickets.edit', ticket.uid || ticket.id)"
              >
                {{ ticket.status }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4 focus:text-indigo-500"
                :href="route('tickets.edit', ticket.uid || ticket.id)"
              >
                {{ __('error') === 'error' ? moment(ticket.created_at).fromNow() : moment(ticket.created_at).locale('zh-tw').fromNow() }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4 focus:text-indigo-500"
                :href="route('tickets.edit', ticket.uid || ticket.id)"
              >
                {{ __('error') === 'error' ? moment(ticket.updated_at).fromNow() : moment(ticket.updated_at).locale('zh-tw').fromNow() }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4 focus:text-indigo-500"
                :href="route('tickets.edit', ticket.uid || ticket.id)"
              >
                {{ ticket.category ? ticket.category : __("No category assigned") }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4 focus:text-indigo-500"
                :href="route('tickets.edit', ticket.uid || ticket.id)"
              >
                {{ ticket.department ? ticket.department : __("No department assigned") }}
              </Link>
            </td>
          </tr>
          <tr v-if="tickets.data.length === 0">
            <td class="border-t px-6 py-4" colspan="9">
              {{ __('No ticket found.') }}
            </td>
          </tr>
        </table>
      </div>
      <pagination class="mt-4" :links="tickets.links" />
    </div>
  </template>
  
  <script>
  import { Link, Head } from '@inertiajs/vue3'
  import Icon from '@/Shared/Icon'
  import pickBy from 'lodash/pickBy'
  import Layout from '@/Shared/Layout'
  import throttle from 'lodash/throttle'
  import mapValues from 'lodash/mapValues'
  import Pagination from '@/Shared/Pagination'
  import SelectInput from '@/Shared/SelectInput'
  import SearchInput from '@/Shared/SearchInput'
  import SelectInputFilter from '@/Shared/SelectInputFilter'
  import moment from 'moment'
  import axios from 'axios'
  
  export default {
    metaInfo: { title: 'Tickets' },
    components: {
        SearchInput,
        Icon,
        Link,
        Head,
        Pagination,
        SelectInputFilter,
        SelectInput,
    },
    layout: Layout,
    props: {
        filters: Object,
        tickets: Object,
        assignees: Array,
        auth: Object,
        title: String,
        priorities: Array,
        statuses: Array,
        types: Array,
        categories: Array,
        departments: Array,
        hidden_fields: Object,
        customers: Array,
        export_import_allowed: Boolean,
        total_tickets_amount: Number,
    },
    remember: 'form',
    data() {
        return {
            headers: [
                { name: 'Key', value: 'id', sort: true },
                { name: 'Subject', value: 'subject', sort: true },
                { name: 'Priority', value: 'priority_id', sort: true },
                { name: 'Status', value: 'status_id', sort: true },
                { name: 'Date', value: 'created_at', sort: true },
                { name: 'Updated', value: 'updated_at', sort: true },
                { name: 'Category', value: 'category_id', sort: true },
                { name: 'Department', value: 'department_id', sort: true },

            ],
            user_access: this.$page.props.auth.user.access,
            assigness: [...this.assignees],
            customersData: [...this.customers], // Copy the prop into local data
            form: {
                search: this.filters.search,
                limit: this.filters.limit ?? 10,
                field: this.filters.field,
                direction: this.filters.direction,
                priority_id: this.filters.priority_id ?? null,
                status_id: this.filters.status_id ?? null,
                type_id: this.filters.type_id ?? null,
                category_id: this.filters.category_id ?? null,
                department_id: this.filters.department_id ?? null,
                assigned_to: this.filters.assigned_to ?? null,
                customer_id: this.filters.customer_id ?? null,
            },
        };
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get(
                    this.route('tickets'),
                    pickBy(this.form),
                    { replace: true, preserveState: true }
                );
            }, 150),
        },
    },
    methods: {
        doFilter(e) {
            axios.get(this.route('filter.assignees', { search: e.target.value }))
                .then((res) => {
                    this.assignees.splice(0, this.assignees.length, ...res.data);
                });
        },
        doFilterCustomers(e) {
            axios.get(this.route('filter.customers', { search: e.target.value }))
                .then((res) => {
                    this.customersData.splice(0, this.customersData.length, ...res.data);
                });
        },
        sort(field) {
            this.form.field = field;
            this.form.direction = this.form.direction === 'asc' ? 'desc' : 'asc';
        },
        reset() {
            this.form = mapValues(this.form, () => null);
        },
        resetFormKey(key) {
          this.form[key] = null;
        },
        uploadImportCSV(e) {
            if (e.target.files.length) {
                this.$inertia.form({ file: e.target.files[0] }).post(this.route('ticket.csv.import'));
            }
        },
    },
    created() {
        this.moment = moment;

        if (this.filters.customer_id) {
          axios.get(this.route('filter.customerbyid', {id: this.filters.customer_id})).then((res)=>{

            this.form.customer_id = Number(this.form.customer_id);

            this.customersData.splice(0, this.customersData.length, ...res.data);

          });
        }

        if (this.filters.assigned_to) {
          axios.get(this.route('filter.assigneebyid', {id: this.filters.assigned_to})).then((res)=>{

            this.form.assigned_to = Number(this.form.assigned_to);

            this.assignees.splice(0, this.assignees.length, ...res.data);

          });
        }

    }
  };
  </script>
  